import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ambulance, UserRound } from 'lucide-react';
import { get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { safeReturnTo } from '../opd/BookAppointmentPage';

interface VehicleOption {
  id: string;
  registrationNo: string;
  isActive: boolean;
}

export function DispatchTripPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), '/ambulance');
  const initialPatientId = params.get('patientId') ?? '';
  const [form, setForm] = useState({ vehicleId: '', patientId: initialPatientId, pickup: '', dropoff: '' });
  const [error, setError] = useState<string | null>(null);

  const vehicles = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => get<VehicleOption[]>('/ambulance/vehicles'),
  });

  const dispatch = useMutation({
    mutationFn: () =>
      post('/ambulance/trips', {
        vehicleId: form.vehicleId,
        patientId: form.patientId || null,
        pickup: form.pickup,
        dropoff: form.dropoff,
      }),
    onSuccess: () => {
      toast.success('Dispatched');
      void queryClient.invalidateQueries({ queryKey: ['trips'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const valid = Boolean(form.vehicleId) && form.pickup.length >= 2 && form.dropoff.length >= 2;
  const dirty = !dispatch.isSuccess && (form.vehicleId !== '' || form.patientId !== initialPatientId || form.pickup !== '' || form.dropoff !== '');

  return (
    <FormScreen
      title="Dispatch an ambulance"
      description="A vehicle already out cannot be sent again"
      icon={Ambulance}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        setError(null);
        dispatch.mutate();
      }}
    >
      <FormSection title={tr('ipd', 'route')} icon={Ambulance}>
        <Field label="Vehicle" required className="sm:col-span-2">
          <Select value={form.vehicleId} onChange={(e) => setForm({ ...form, vehicleId: e.target.value })}>
            <option value="">Select…</option>
            {vehicles.data
              ?.filter((v) => v.isActive)
              .map((vehicle) => (
                <option key={vehicle.id} value={vehicle.id}>
                  {vehicle.registrationNo}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Pick up" required>
          <Input value={form.pickup} onChange={(e) => setForm({ ...form, pickup: e.target.value })} />
        </Field>
        <Field label="Drop off" required>
          <Input value={form.dropoff} onChange={(e) => setForm({ ...form, dropoff: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'patient')} icon={UserRound} columns={1}>
        <Field label="Patient" hint="Optional — a transfer may have no record yet">
          <PatientPicker value={form.patientId} onChange={(patientId) => setForm({ ...form, patientId })} />
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel="Dispatch" loading={dispatch.isPending} incomplete={!valid} cancelTo={returnTo} />
    </FormScreen>
  );
}
